/**
 * Timezone-aware day utilities used by the DatePicker components.
 *
 * Strategy: keep `Date` values internally, but normalize to "local midnight"
 * before any comparison so DST shifts and tz boundaries can't make two
 * "same day" dates differ by hours. When a caller wants strict tz handling
 * later, swap toMidnight for an Intl.DateTimeFormat-driven implementation.
 */

export function toMidnight(d: Date | null | undefined): Date | null {
  if (!d) return null;
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

export function sameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  const am = toMidnight(a)!;
  const bm = toMidnight(b)!;
  return (
    am.getFullYear() === bm.getFullYear() &&
    am.getMonth() === bm.getMonth() &&
    am.getDate() === bm.getDate()
  );
}

export function isBetween(d: Date | null, s: Date | null, e: Date | null): boolean {
  if (!d || !s || !e) return false;
  const dt = toMidnight(d)!.getTime();
  const st = toMidnight(s)!.getTime();
  const et = toMidnight(e)!.getTime();
  return dt > Math.min(st, et) && dt < Math.max(st, et);
}

export function fmtDisplay(d: Date | null | undefined): string {
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${fmtDisplay(d)} ${hh}:${mi}`;
}

export function getDecadeStart(y: number): number {
  return Math.floor(y / 10) * 10;
}

export function getCenturyStart(y: number): number {
  return Math.floor(y / 100) * 100;
}

export const DAY_HEADERS = ["S", "M", "T", "W", "T", "F", "S"] as const;
export const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;
export const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;
